import React, { useEffect, useState, useCallback, memo } from 'react';
import { View, FlatList, Text, StyleSheet, TouchableOpacity, TextInput, Alert } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { getNotesData, saveNote, deleteNote, updateNote } from '../../../api/Notes';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useConfirmModal, useAlertModal } from '../../../common/hooks';
import { msgStr } from '../../../common/constants/Message';

interface Note {
  id: string;
  text: string;
  updatedAt: string;
  author?: string;
}

interface Props {
  reservationInfo: any;
}

const NotesTab = ({ reservationInfo }: Props) => {
  const [notesData, setNotesData] = useState<Note[]>([]);
  const [editText, setEditText] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const { showConfirm } = useConfirmModal();
  const { showAlert } = useAlertModal();
  
  useEffect(() => {
    const fetchUserId = async () => {
      const storedUserId = await AsyncStorage.getItem('user-id');
      setUserId(storedUserId);
    };
    fetchUserId();
  }, []);

  const fetchNotes = useCallback(() => {
    if (!reservationInfo?.order_number) return;
    
    getNotesData({ order_number: reservationInfo.order_number }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setNotesData(jsonRes || []);
          break;
        default:
          setNotesData([]);
          break;
      }
    });
  }, [reservationInfo?.order_number]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const startEditingNote = (note: Note) => {
    setIsEditingNote(true);
    setEditingNoteId(note.id);
    setEditText(note.text);
  };

  const cancelEditing = () => {
    setIsEditingNote(false);
    setEditingNoteId(null);
    setEditText('');
  };

  const addNewNote = () => {
    setIsAddingNote(true);
    setEditText('');
  };

  const cancelNewNote = () => {
    setIsAddingNote(false);
    setEditText('');
  };


  const saveEditedNote = useCallback(() => {
    if (!editingNoteId || !editText.trim() || !userId) return;

    const noteToUpdate = notesData.find(note => note.id === editingNoteId);
    if (!noteToUpdate) return;

    const updatedNote = {
      ...noteToUpdate,
      user_id: userId,
      order_number: reservationInfo.order_number,
      text: editText.trim(),
    };

    updateNote(updatedNote, (response, status) => {
      if (status === 201) {
        setIsEditingNote(false);
        setEditingNoteId(null);
        setEditText('');
        fetchNotes();
      } else {
        Alert.alert('Error', 'Failed to save note');
      }
    });
  }, [editingNoteId, editText, userId, notesData, reservationInfo.order_number, fetchNotes]);

  const confirmDeleteNote = useCallback((id: string) => {

    // showConfirm(msgStr('deleteConfirmStr'), () => {
      deleteNote(id, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            fetchNotes();
            // showAlert('success', jsonRes.message);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    // });
  }, [showConfirm, showAlert, fetchNotes]);

  const saveNewNote = useCallback(() => {
    if (!editText.trim() || !userId) return;

    const newNote = {
      order_number: reservationInfo.order_number,
      user_id: userId,
      text: editText.trim(),
    };

    saveNote(newNote, (response, status) => {
      if (status === 201) {
        setIsAddingNote(false);
        setEditText('');
        fetchNotes();
      } else {
        Alert.alert('Error', 'Failed to save note');
      }
    });
  }, [editText, userId, reservationInfo.order_number, fetchNotes]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderNoteItem = useCallback(({ item: note }) => (
    <View style={styles.noteContainer}>
      <View style={{flexDirection:'row', justifyContent:'space-between', alignItems:"flex-start", gap: 8}}>
        <Text style={styles.noteText}>
          {note.text}
        </Text>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <TouchableOpacity 
            style={styles.noteActionButton}
            onPress={() => startEditingNote(note)}
          >
            <FontAwesome5 name="edit" size={14} color="#333" />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.noteActionButton}
            onPress={() => confirmDeleteNote(note.id)}
          >
            <FontAwesome5 name="trash" size={14} color="#333" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  ), [confirmDeleteNote]);

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.notesHeader}>
        <FontAwesome5 name="sticky-note" size={18} color="#333" />
        <Text style={styles.notesCount}>
          {notesData.length} note{notesData.length !== 1 ? 's' : ''}
        </Text>
      </View>
      <TouchableOpacity style={styles.addButton} onPress={addNewNote}>
          <View style={{flexDirection:'row', alignItems:'center'}}>
            <FontAwesome5 name="plus" size={14} color="white" style={{marginTop:3}}/>
            <Text style={styles.addButtonText}>Add Note</Text>
          </View>
      </TouchableOpacity>
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.emptyState}>
      <FontAwesome5 name="sticky-note" size={48} color="#ccc" />
      <Text style={styles.emptyText}>No notes available</Text>
    </View>
  );

  const renderAddNoteForm = () => (
    <View style={styles.addNoteForm}>
      <View style={styles.addNoteHeader}>
        <Text style={styles.addNoteTitle}>Add New Note</Text>
      </View>
      <TextInput
        style={styles.addNoteInput}
        placeholder="Enter your note here..."
        value={editText}
        onChangeText={setEditText}
        multiline
        textAlignVertical="top"
      />
      <View style={styles.addNoteActions}>
        <TouchableOpacity style={styles.addButton} onPress={cancelNewNote}>
          <Text style={styles.addButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.addButton} onPress={saveNewNote}>
          <Text style={styles.addButtonText}>Save Note</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderEditNoteForm = () => {
    const noteBeingEdited = notesData.find(note => note.id === editingNoteId);
    
    return (
      <View style={styles.addNoteForm}>
        <View style={styles.addNoteHeader}>
          <Text style={styles.addNoteTitle}>Edit Note</Text>
          {noteBeingEdited && (
            <Text style={styles.noteTimestampInForm}>
              {formatTimestamp(noteBeingEdited.updatedAt)}
            </Text>
          )}
        </View>
        <TextInput
          style={styles.addNoteInput}
          placeholder="Enter your note here..."
          value={editText}
          onChangeText={setEditText}
          multiline
          textAlignVertical="top"
        />
        <View style={styles.addNoteActions}>
          <TouchableOpacity style={styles.addButton} onPress={cancelEditing}>
            <Text style={styles.addButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.addButton} onPress={saveEditedNote}>
            <Text style={styles.addButtonText}>Update Note</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {isAddingNote ? (
        renderAddNoteForm()
      ) : isEditingNote ? (
        renderEditNoteForm()
      ) : (
        <FlatList
          data={notesData}
          renderItem={renderNoteItem}
          keyExtractor={(item) => item.id}
          ListHeaderComponent={renderHeader}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={styles.flatListContent}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
          style={styles.flatList}
        />
      )}
    </View>
  );
};


const styles = StyleSheet.create({
  addButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    alignItems: 'center',
    borderRadius: 3,
    borderWidth: 0,
    borderColor: '#6c757d',
    backgroundColor: '#2e96e1',
  },
  noteContainer: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e1e8ed',
  },
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
  },
  flatList: {
    flex: 1,
    width: '100%',
  },
  flatListContent: {
    padding: 16,
    paddingBottom: 40,
  },
  header: {
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  notesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  notesCount: {
    fontSize: 16,
    marginLeft: 8,
    color: '#333',
    fontWeight: '500',
  },
  addButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  noteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  noteTimestamp: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  noteActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  noteActionButton: {
    padding: 4,
  },
  noteAuthor: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 4,
  },
  noteText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    minHeight: 200,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    color: '#999',
    marginBottom: 20,
  },
  emptyAddButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
  },
  emptyAddButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '500',
  },
  // Add Note Form
  addNoteForm: {
    flex: 1,
    padding: 16,
  },
  addNoteHeader: {
    marginBottom: 16,
  },
  addNoteTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  noteTimestampInForm: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  addNoteInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#333',
    backgroundColor: '#f8f9fa',
    marginBottom: 16,
  },
  addNoteActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#f0f0f0',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 14,
    fontWeight: '500',
  },
  saveButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#007AFF',
  },
  saveButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '500',
  },
});

export default NotesTab;